import { attemptTiming, type Attempt, type Fluency } from "../responses/attempt.ts";

export interface FluencyPolicy {
  readonly version: string;
  readonly minimumBaselineSamples: number;
  readonly fluentMultiplier: number;
  readonly toleranceMs: number;
}

export const DEFAULT_FLUENCY_POLICY: FluencyPolicy = {
  version: "personal-retrieval-fluency-v1",
  minimumBaselineSamples: 5,
  fluentMultiplier: 1.5,
  toleranceMs: 400,
};

export interface RetrievalBaseline {
  readonly sampleCount: number;
  readonly medianThinkingTimeMs: number | null;
  readonly fluentUpperBoundMs: number | null;
  readonly policyVersion: string;
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

export function buildRetrievalBaseline(
  attempts: readonly Attempt[],
  policy: FluencyPolicy = DEFAULT_FLUENCY_POLICY,
): RetrievalBaseline {
  const samples = attempts
    .filter((attempt) =>
      attempt.responseMode === "DIRECT_RETRIEVAL"
      && attempt.correct === true
      && !attempt.helpRequested
      && !attempt.supportUsed
      && attempt.editCount === 0
    )
    .map((attempt) => attemptTiming(attempt).thinkingTimeMs)
    .filter((value): value is number => value !== null);

  if (samples.length < policy.minimumBaselineSamples) {
    return {
      sampleCount: samples.length,
      medianThinkingTimeMs: null,
      fluentUpperBoundMs: null,
      policyVersion: policy.version,
    };
  }

  const medianThinkingTimeMs = median(samples);
  return {
    sampleCount: samples.length,
    medianThinkingTimeMs,
    fluentUpperBoundMs: medianThinkingTimeMs * policy.fluentMultiplier + policy.toleranceMs,
    policyVersion: policy.version,
  };
}

export function classifyRetrievalFluency(
  attempt: Attempt,
  baseline: RetrievalBaseline,
): Fluency {
  if (
    attempt.responseMode !== "DIRECT_RETRIEVAL"
    || attempt.correct !== true
    || attempt.helpRequested
    || attempt.supportUsed
  ) return "INVALID";

  const thinkingTimeMs = attemptTiming(attempt).thinkingTimeMs;
  if (thinkingTimeMs === null || baseline.fluentUpperBoundMs === null) {
    return "UNASSESSED";
  }
  return thinkingTimeMs <= baseline.fluentUpperBoundMs ? "FLUENT" : "SLOW";
}
